import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { ProductCardData } from '../../product-card/product-card-display';
import { StoreState } from '../../../store/store.interface';
import { WishListAnalytics } from '../utils/wish-list-analytics.util';
import { WishListCommentElement } from '../interfaces/wish-list-comment.interface';
import { WishListCommentUtil } from '../utils/wish-list-comment.util';
import { clearUpdateProductOnWishListComment, setUpdateProductOnWishListComment } from '../store/wish-list.store';
import { connect } from 'pwa-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { getUpdateProductOnWishListComment } from '../store/wish-list.selector';
import { html, TemplateResult } from 'lit';
import { shouldClearOffCanvasElements } from '../../off-canvas/utils/off-canvas.util';

@customElement('mhecomm-wish-list-update-product-comment')
export class WishListUpdateProductComment extends connect(store)(BaseComponent) implements WishListCommentElement {
  /**
   * The maximum size of the comment.
   */
  @property({ type: Number })
  commentMaxSize = 300;

  /**
   * If the component is used as an off-canvas, this should be set to the off-canvas ID.
   */
  @property()
  offCanvasId = '';

  /**
   * The placeholder text for the comment text area.
   */
  @property()
  placeholderComment = 'Type to add a comment';

  /**
   * The product related to the comment.
   */
  @property({ type: Object })
  product?: ProductCardData;

  /**
   * The wish list ID related to the comment.
   */
  @property({ type: Number })
  wishListId = 0;

  /**
   * The value of the comment text area.
   */
  @state()
  private comment = '';

  /**
   * The size of the comment.
   */
  @state()
  private commentSize = 0;

  /**
   * A flag to hold the state of the errors.
   */
  @state()
  private errors = false;

  /**
   * A flag to indicate if the comment section is expanded.
   */
  @state()
  private isCommentExpanded = false;

  /**
   * The utility class for handling comment-related functionalities.
   */
  private commentUtil: WishListCommentUtil;

  /**
   * The collapse instance for the comment section.
   */
  private collapse: any;

  /**
   * The identifier for comment elements in the DOM.
   */
  private identifier = 'wish-list-update-product';

  /**
   * The analytics instance for the wish list.
   */
  private readonly analytics: WishListAnalytics;

  constructor() {
    super();
    this.analytics = new WishListAnalytics();
    this.commentUtil = new WishListCommentUtil(this, this.identifier);
  }

  /**
   * @param state
   */
  stateChanged(state: StoreState): void {
    this.updateCommentFromState(state);
  }

  renderComp(): TemplateResult {
    return html`
      <form action="#" method="POST">
        ${this.commentUtil.renderCollapseToggleButton(this.isCommentExpanded, this.comment)}
        ${this.commentUtil.renderCommentValue(this.isCommentExpanded, this.comment)}
        ${this.commentUtil.renderCommentCollapse(
          this.commentSize,
          this.commentMaxSize,
          this.errors,
          this.placeholderComment
        )}
      </form>
    `;
  }

  /**
   * Updates the temporary comment state from the input event.
   *
   * @param event
   */
  updateCommentFromInput(event: InputEvent): void {
    const comment = event.target as HTMLInputElement;

    this.comment = comment.value ?? '';
    this.commentSize = comment.value.length ?? 0;
  }

  /**
   * Updates the comment from the Redux store.
   *
   * When the action is dispatched to clear the off-canvas elements, the comment is cleared.
   *
   * This happens when the off-canvas is closed.
   *
   * @param state
   */
  private updateCommentFromState(state: StoreState): void {
    this.comment = getUpdateProductOnWishListComment(state);
    if (!this.comment) {
      this.commentUtil.clearCommentInput(this.shadowRoot!);
      this.commentSize = 0;
    }

    if (shouldClearOffCanvasElements(this.offCanvasId, state)) {
      store.dispatch(clearUpdateProductOnWishListComment());
    }
  }

  /**
   * Saves the comment of the product to be added to the wish list.
   */
  saveComment(): void {
    this.errors = false;

    if (!this.commentUtil.isValidComment(this.comment)) {
      this.errors = true;
      return;
    }

    this.analytics.comment(this, this.comment, this.wishListId, this.product?.isbn ?? '', 'Save Comment');
    store.dispatch(setUpdateProductOnWishListComment(this.comment));
    this.toggleCollapse();
  }

  /**
   * Collapses the comment section and clears the comment on the Redux store.
   */
  cancelComment(): void {
    this.analytics.comment(this, '', this.wishListId, this.product?.isbn ?? '', 'Cancel', true);
    this.toggleCollapse();
    store.dispatch(clearUpdateProductOnWishListComment());
  }

  /**
   * Handles the collapse toggle event.
   */
  handleCollapseToggle(event: Event): void {
    event.preventDefault();
    this.toggleCollapse();
  }

  /**
   * Expends or collapses the comment section.
   */
  private toggleCollapse(): void {
    this.collapse?.toggle();
    this.isCommentExpanded = !this.isCommentExpanded;
  }

  /**
   * Initializes the collapse instance for the comment section.
   */
  updated(): void {
    this.collapse = this.bsInit(`#${this.identifier}-comment`, window.bootstrap?.Collapse, { toggle: false })?.instance;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-wish-list-update-product-comment': WishListUpdateProductComment;
  }
}
